import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  model,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TranslateLangPipe, IconComponent } from '@gs-2.0/shared';

const SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectFieldComponent),
  multi: true,
};

const SELECT_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => SelectFieldComponent),
  multi: true,
};

@Component({
  selector: 'app-select-field',
  standalone: true,
  imports: [NgClass, SelectModule, FormsModule, TranslateLangPipe, IconComponent],
  providers: [SELECT_VALUE_ACCESSOR, SELECT_VALIDATOR],
  templateUrl: './select-field.component.html',
  styleUrl: './select-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectFieldComponent<T = any> implements ControlValueAccessor, Validator {
  // Core inputs
  label = input<string>('');
  placeholder = input<string>('');
  errorMessage = input<string>('');
  required = input<boolean>(false);
  disabled = signal(false);
  options = input<T[]>([]);
  optionLabel = input<string>('name');
  optionValue = input<string | undefined>(undefined);

  // Display & behavior
  showClear = input<boolean>(false);
  filter = input<boolean>(false);
  filterPlaceholder = input<string>('');
  ariaLabel = input<string>('Select');
  validationDelay = input<number>(300);
  fluid = input<boolean>(true);
  checkmark = input<boolean>(false);

  // Outputs
  change = output<T>();
  blur = output<void>();
  focus = output<void>();
  clear = output<void>();

  // Internal state
  value = model<T | null>(null);
  isTouched = signal(false);
  showDelayedError = signal(false);
  private validationTimer: any = null;

  // Computed
  isInvalid = computed(() =>
    this.required() && !this.value() && this.isTouched() && this.showDelayedError()
  );

  effectiveAriaLabel = computed(() => this.ariaLabel() || this.label());

  // ControlValueAccessor callbacks
  private onChangeCallback: (value: T | null) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  constructor() {
    // Sync value changes to form control
    effect(() => {
      const currentValue = this.value();
      queueMicrotask(() => {
        this.onChangeCallback(currentValue);
      });
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: T | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required() && !control.value) {
      return { required: true };
    }
    return null;
  }

  // Event handlers
  onBlur(): void {
    this.isTouched.set(true);
    this.onTouchedCallback();
    this.blur.emit();

    // Start delayed validation
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.validationTimer = setTimeout(() => {
      this.showDelayedError.set(true);
    }, this.validationDelay());
  }

  onFocus(): void {
    // Clear validation timer and reset error on focus
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.showDelayedError.set(false);
    this.focus.emit();
  }

  onChange(event: any): void {
    this.value.set(event.value);
    this.showDelayedError.set(false);
    this.change.emit(event.value);
  }

  onClear(): void {
    this.value.set(null);
    this.clear.emit();
  }
}
